import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useSearchParams } from 'react-router-dom';

import { Mail, Phone, MapPin, Linkedin, Github, User, Languages, Globe, Download, ArrowLeft } from 'lucide-react';
import Portfolio from './Portfolio';
import ATSCV from './ATSCV';
import { cvTranslations } from './translations';
import './App.mobile.css';

const ACCENT = '#4f46e5';
const INK = '#0f172a';

const ModernCV = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [photoUrl] = useState('/mecopie.jpeg');

  const getInitialLanguage = () => {
    const langParam = searchParams.get('lang');
    if (langParam === 'en' || langParam === 'fr') return langParam;
    return 'fr';
  };

  const [language, setLanguage] = useState(getInitialLanguage);
  const componentRef = useRef();

  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (langParam === 'en' || langParam === 'fr') setLanguage(langParam);
  }, [searchParams]);

  const t = cvTranslations[language];

  const experiences = t.experiences.map((exp, index) => ({
    ...exp,
    current: index === 0,
  }));

  const education = t.education_items;
  const projects = t.projects_items;
  const skills = t.skills_categories;
  const spokenLanguages = t.languages_items;

  const handleDownloadPDF = () => window.print();

  const SectionTitle = ({ children }) => (
    <h2 className="cv-section-title flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.12em] pb-1 mb-2.5" style={{ color: INK }}>
      <span className="inline-block h-[12px] w-[3px] rounded-sm" style={{ background: ACCENT }} />
      <span>{children}</span>
      <span className="ml-1 h-px flex-1" style={{ background: '#e2e8f0' }} />
    </h2>
  );

  return (
    <div className="cv-page min-h-screen py-6 px-2 md:py-10 md:px-4 print:p-0" style={{ background: '#eef0f4', fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
      <article ref={componentRef} lang={language} className="cv-container mx-auto bg-white print:shadow-none print:max-w-none" style={{ maxWidth: 820, borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 70px -30px rgba(15,23,42,0.35)' }}>

        {/* ===== HEADER ===== */}
        <header className="cv-header relative px-7 py-4 md:px-10 md:py-5 text-white" style={{ background: 'linear-gradient(120deg,#111827 0%,#1e293b 55%,#312e81 130%)' }}>
          <div className="cv-header-inner flex items-center gap-5">
            <div className="cv-photo h-[74px] w-[74px] flex-shrink-0 overflow-hidden rounded-full md:h-[82px] md:w-[82px]" style={{ border: '3px solid rgba(255,255,255,0.25)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
              {photoUrl ? (
                <img src={photoUrl} alt="Nizar Tazi" className="h-full w-full object-cover" draggable={false} />
              ) : (
                <div className="flex h-full w-full items-center justify-center" style={{ background: '#475569' }}>
                  <User size={32} className="text-slate-300" />
                </div>
              )}
            </div>

            <div className="min-w-0">
              <h1 className="text-[25px] font-extrabold leading-tight tracking-tight md:text-[30px]">Nizar Tazi</h1>
              <p className="mt-0.5 text-[13px] font-medium tracking-wide md:text-[14px]" style={{ color: '#c7d2fe' }}>{t.title}</p>

              <div className="cv-contact mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: '#cbd5e1' }}>
                <span className="flex items-center gap-1"><MapPin size={11} style={{ color: '#818cf8' }} />Tanger, Maroc</span>
                <span style={{ color: '#475569' }}>|</span>
                <a href="mailto:Nizar.tazi@hotmail.fr" className="flex items-center gap-1 transition-colors hover:text-white"><Mail size={11} style={{ color: '#818cf8' }} />Nizar.tazi@hotmail.fr</a>
                <span style={{ color: '#475569' }}>|</span>
                <a href="tel:+212779168753" className="flex items-center gap-1 transition-colors hover:text-white"><Phone size={11} style={{ color: '#818cf8' }} />+212779168753</a>
                <span style={{ color: '#475569' }}>|</span>
                <a href="https://linkedin.com/in/nizar-tazi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 transition-colors hover:text-white"><Linkedin size={11} style={{ color: '#818cf8' }} />linkedin.com/in/nizar-tazi</a>
                <span style={{ color: '#475569' }}>|</span>
                <a href="https://github.com/NizarTazi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 transition-colors hover:text-white"><Github size={11} style={{ color: '#818cf8' }} />github.com/NizarTazi</a>
                <span style={{ color: '#475569' }}>|</span>
                <a href="https://nizart.works" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 transition-colors hover:text-white"><Globe size={11} style={{ color: '#818cf8' }} />nizart.works</a>
              </div>
            </div>
          </div>

          <button
            onClick={handleDownloadPDF}
            className="no-print absolute right-6 top-5 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11.5px] font-semibold text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)' }}
          >
            <Download size={12} />
            {t.downloadPDF}
          </button>
        </header>

        {/* ===== BODY ===== */}
        <div className="cv-body px-7 py-4 md:px-10 md:py-5" style={{ color: '#334155' }}>

          {/* PROFILE */}
          <section className="mb-5 cv-section">
            <SectionTitle>{t.profile}</SectionTitle>
            <p className="text-[10.5px] leading-[1.45]" style={{ color: '#475569' }}>{t.profileText1}</p>
          </section>

          {/* EXPERIENCE */}
          <section className="mb-5 cv-section">
            <SectionTitle>{t.experience}</SectionTitle>
            <div className="space-y-3">
              {experiences.map((exp, index) => (
                <div key={index} className="cv-entry">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <h3 className="text-[11.5px] font-bold" style={{ color: INK }}>{exp.title}</h3>
                    <span className="whitespace-nowrap text-[10px] font-medium" style={{ color: '#64748b' }}>{exp.period}</span>
                  </div>
                  <p className="mb-0.5 text-[10.5px]" style={{ color: '#475569' }}>
                    <span className="font-semibold" style={{ color: ACCENT }}>{exp.company}</span>
                    <span style={{ color: '#94a3b8' }}> | {exp.location}</span>
                    {exp.current && (
                      <span className="ml-1.5 inline-block rounded px-1.5 py-px text-[8px] font-bold text-white align-middle" style={{ background: ACCENT }}>{t.current}</span>
                    )}
                  </p>
                  <ul className="space-y-0.5 pl-4">
                    {exp.points.map((point, i) => (
                      <li key={i} className="cv-bullet text-[10.5px] leading-[1.4]" style={{ color: '#475569' }}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* PROJECTS */}
          <section className="mb-5 cv-section">
            <SectionTitle>{t.projects}</SectionTitle>
            <ul className="space-y-1.5 pl-4">
              {projects.map((project, index) => (
                <li key={index} className="cv-bullet cv-entry text-[10.5px] leading-[1.45]" style={{ color: '#475569' }}>
                  <span className="font-bold" style={{ color: INK }}>{project.title}</span>
                  <span style={{ color: '#94a3b8' }}> | </span>
                  {project.description}
                  {project.links && (
                    <span>
                      {' ('}
                      {project.links.map((link, i) => (
                        <span key={i}>
                          {i > 0 && <span style={{ color: '#94a3b8' }}>, </span>}
                          <a href={`https://${link}`} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: ACCENT }}>{link}</a>
                        </span>
                      ))}
                      {')'}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {/* SKILLS */}
          <section className="mb-5 cv-section">
            <SectionTitle>{t.skills}</SectionTitle>
            <div className="cv-skills-grid grid grid-cols-1 gap-x-6 gap-y-1.5 md:grid-cols-2">
              {Object.entries(skills).map(([category, items]) => (
                <div key={category} className="cv-entry text-[10.5px] leading-[1.4]">
                  <span className="font-bold" style={{ color: ACCENT }}>{category}: </span>
                  <span style={{ color: '#475569' }}>{items.join(', ')}</span>
                </div>
              ))}
            </div>
          </section>

          {/* EDUCATION */}
          <section className="mb-5 cv-section">
            <SectionTitle>{t.education}</SectionTitle>
            <div className="space-y-1.5">
              {education.map((edu, index) => (
                <div key={index} className="cv-entry flex items-baseline justify-between gap-3">
                  <p className="text-[10.5px] leading-[1.35]">
                    <span className="font-bold" style={{ color: INK }}>{edu.degree}</span>
                    <span style={{ color: '#94a3b8' }}> | {edu.school}</span>
                  </p>
                  <span className="whitespace-nowrap text-[10px] font-medium" style={{ color: '#64748b' }}>{edu.year}</span>
                </div>
              ))}
            </div>
          </section>

          {/* LANGUAGES */}
          <section className="mb-5 cv-section">
            <SectionTitle>{t.languages}</SectionTitle>
            <p className="flex flex-wrap gap-x-6 gap-y-1 text-[10.5px] leading-[1.35]">
              {spokenLanguages.map((lang, index) => (
                <span key={index} style={{ color: '#475569' }}>
                  <span className="font-bold" style={{ color: INK }}>{lang.lang}</span>
                  <span style={{ color: '#94a3b8' }}> | </span>
                  <span>{lang.level}</span>
                </span>
              ))}
            </p>
          </section>

        </div>
      </article>

      {/* Floating Action Buttons */}
      <div className="cv-actions fixed bottom-6 right-6 z-[9999] no-print no-pdf flex flex-col items-end gap-2.5">
        <div className="group relative">
          <button className="flex items-center gap-2 rounded-lg px-3.5 py-2.5 text-sm font-semibold text-white shadow-xl transition-all" style={{ background: 'linear-gradient(135deg,#4f46e5,#6d5efc)' }}>
            <Languages className="h-4 w-4" />
            {language.toUpperCase()}
          </button>
          <div className="invisible absolute bottom-full right-0 mb-2 w-32 overflow-hidden rounded-lg border border-slate-200 bg-white opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:opacity-100">
            <button onClick={() => { setLanguage('en'); setSearchParams({ lang: 'en' }); }} className={`block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${language === 'en' ? 'bg-slate-100 font-semibold' : ''}`}>English</button>
            <button onClick={() => { setLanguage('fr'); setSearchParams({ lang: 'fr' }); }} className={`block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${language === 'fr' ? 'bg-slate-100 font-semibold' : ''}`}>Français</button>
          </div>
        </div>
        <Link to="/" className="flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-xl transition-all hover:shadow-2xl" style={{ background: '#0f172a' }}>
          <ArrowLeft className="h-4 w-4" />
          {t.portfolio}
        </Link>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 8mm 0 6mm 0; }
          @page :first { margin-top: 0; }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .no-print, .no-pdf, .cv-actions { display: none !important; }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            font-size: 12px !important;
          }

          .cv-page { padding: 0 !important; margin: 0 !important; background: #fff !important; min-height: auto !important; }
          .cv-container { box-shadow: none !important; max-width: none !important; width: 100% !important; margin: 0 !important; border-radius: 0 !important; }
          .cv-header { background: linear-gradient(120deg,#111827 0%,#1e293b 55%,#312e81 130%) !important; }

          .cv-entry { break-inside: avoid; page-break-inside: avoid; }
          .cv-section-title { break-after: avoid; page-break-after: avoid; }

          a { color: inherit !important; text-decoration: none !important; }
          a[href^="http"]::after { content: none !important; }
        }

        .cv-bullet { position: relative; list-style: none; padding-left: 0.85rem; }
        .cv-bullet::before {
          content: ''; position: absolute; left: 0; top: 0.46rem;
          width: 4px; height: 4px; border-radius: 50%; background: ${ACCENT};
        }
      `}</style>
    </div>
  );
};

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<Portfolio />} />
          <Route path="/CV" element={<ModernCV />} />
          <Route path="/CV/ats" element={<ATSCV />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
